"use client"; 

import { useContext } from 'react';
import CustomButton from './CustomButton';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import ModalWrapper from './ModalWrapper';
import { Warning } from './Icons';
import { useUrls, pathTopicAndActivity } from '@/utils/urls';
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';
import { OpenerContext } from '@/components/C1Opener/OpenerCommunicator';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFatal?: boolean;
}

export const ErrorModal: React.FC<ModalProps> = ({ isOpen, onClose, isFatal }) => {
  const router = useRouter();
  const URLS = useUrls();
  const isC1Integrated = useIsC1Integrated();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const opener = useContext(OpenerContext);

  if (!isOpen) return null;

  const onRestart = () => {
    onClose();

    /**
     * If we got error during any C1 integrated flow
     * restart should take user to the Goals page to allow the user to restart the activity again.
     */
    if (isC1Integrated) {
      let { activityKey, topicId } = pathTopicAndActivity(pathname);

      if ((!activityKey || !topicId) && pathname === '/report') {
        const backUrl = searchParams.get('backUrl');
        if (backUrl) {
          const backUrlObj = new URL(backUrl, globalThis.location.origin);
          const fromBackUrl = pathTopicAndActivity(backUrlObj.pathname);
          activityKey = fromBackUrl.activityKey ?? activityKey;
          topicId = fromBackUrl.topicId ?? topicId;
        }
      }

      if (activityKey && topicId) {
        router.push(URLS.topicSelectLink(activityKey, topicId));
        return;
      } else if (opener) {
        // In C1, if activity key and topic id is not available, close the hosted activity.
        opener.close();
        return;
      }
    }

    // Redirect the user to the homepage if haven't completed the mic checks, otherwise
    // the user will end up in the activities pages and like that is bypassing the 
    // device checks:
    // @see: https://btsservice.atlassian.net/browse/IS-734
    const userCompletedMicChecks = sessionStorage.getItem('userCompletedMicChecks');
    const userCompletedMicChecksBool = userCompletedMicChecks === 'true';
    router.push(URLS.errorModalRestart(userCompletedMicChecksBool));
  }

  return (
    <ModalWrapper
      onExit={onClose}
      title='Oh no!'
      buttons={[
        <CustomButton 
        key={'restart-button'}
        onClick={onRestart}>
          Restart
        </CustomButton>]}
      iconElement={<Warning />}
      className='error-message-wrapper'
      text='Something has stopped working!'
      hideCloseButton={isFatal}
    />
  );
};
